import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2 } from 'lucide-react';
import AdminSidebar from '../../components/AdminSidebar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import api from '../../utils/api';
import { toast } from 'sonner';

const AdminRooms = () => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image_url: '',
    price_per_night: '',
    max_guests: '',
    amenities: '',
    status: 'available',
  });

  useEffect(() => {
    fetchRooms();
  }, []);

  const fetchRooms = async () => {
    try {
      const response = await api.get('/rooms');
      setRooms(response.data);
    } catch (error) {
      toast.error('Failed to load rooms');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (room = null) => {
    if (room) {
      setEditingRoom(room);
      setFormData({
        name: room.name,
        description: room.description,
        image_url: room.image_url,
        price_per_night: room.price_per_night.toString(),
        max_guests: room.max_guests.toString(),
        amenities: room.amenities.join(', '),
        status: room.status,
      });
    } else {
      setEditingRoom(null);
      setFormData({
        name: '',
        description: '',
        image_url: '',
        price_per_night: '',
        max_guests: '',
        amenities: '',
        status: 'available',
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const roomData = {
        name: formData.name,
        description: formData.description,
        image_url: formData.image_url,
        price_per_night: parseFloat(formData.price_per_night),
        max_guests: parseInt(formData.max_guests),
        amenities: formData.amenities.split(',').map(a => a.trim()).filter(Boolean),
        status: formData.status,
      };

      if (editingRoom) {
        await api.put(`/rooms/${editingRoom.id}`, roomData);
        toast.success('Room updated successfully');
      } else {
        await api.post('/rooms', roomData);
        toast.success('Room created successfully');
      }

      setIsModalOpen(false);
      fetchRooms();
    } catch (error) {
      toast.error('Failed to save room');
    }
  };

  const handleDelete = async (roomId) => {
    if (!window.confirm('Are you sure you want to delete this room?')) return;

    try {
      await api.delete(`/rooms/${roomId}`);
      toast.success('Room deleted successfully');
      fetchRooms();
    } catch (error) {
      toast.error('Failed to delete room');
    }
  };

  return (
    <div className="flex" style={{ background: '#F9F8F6' }}>
      <AdminSidebar />
      <div className="admin-content flex-1" data-testid="admin-rooms">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1
              className="text-4xl font-bold mb-2"
              style={{ fontFamily: 'Playfair Display, serif', color: '#1A3C34' }}
            >
              Rooms
            </h1>
            <p style={{ color: '#64748B' }}>Manage your rooms</p>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="btn-primary flex items-center gap-2"
            style={{ padding: '0.75rem 1.5rem' }}
            data-testid="add-room-button"
          >
            <Plus size={20} />
            Add Room
          </button>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <p style={{ color: '#64748B' }}>Loading rooms...</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rooms.map((room, index) => (
              <motion.div
                key={room.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl overflow-hidden shadow-sm"
                data-testid={`room-card-${index}`}
              >
                <img src={room.image_url} alt={room.name} className="w-full h-48 object-cover" />
                <div className="p-6">
                  <div className="flex items-start justify-between mb-2">
                    <h3
                      className="text-xl font-bold"
                      style={{ fontFamily: 'Playfair Display, serif', color: '#1A3C34' }}
                    >
                      {room.name}
                    </h3>
                    <span
                      className="px-2 py-1 rounded text-xs font-medium"
                      style={{
                        background: room.status === 'available' ? '#8F9E8B20' : '#C67D6320',
                        color: room.status === 'available' ? '#8F9E8B' : '#C67D63',
                      }}
                    >
                      {room.status}
                    </span>
                  </div>
                  <p className="text-sm mb-4" style={{ color: '#64748B' }}>
                    {room.description}
                  </p>
                  <p className="text-2xl font-bold mb-4 mono" style={{ color: '#1A3C34' }}>
                    ₹{room.price_per_night}/night
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleOpenModal(room)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border hover:bg-gray-50 transition-all"
                      style={{ borderColor: '#E5E7EB', color: '#1A3C34' }}
                      data-testid={`edit-room-${index}`}
                    >
                      <Edit size={16} />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(room.id)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border hover:bg-red-50 transition-all"
                      style={{ borderColor: '#E5E7EB', color: '#C67D63' }}
                      data-testid={`delete-room-${index}`}
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-2xl" data-testid="room-modal">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Playfair Display, serif', color: '#1A3C34' }}>
                {editingRoom ? 'Edit Room' : 'Add New Room'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1A202C' }}>
                  Room Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full h-12 px-4 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#1A3C34]/20 transition-all"
                  data-testid="room-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1A202C' }}>
                  Description
                </label>
                <textarea
                  required
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#1A3C34]/20 transition-all"
                  rows="3"
                  data-testid="room-description-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1A202C' }}>
                  Image URL
                </label>
                <input
                  type="url"
                  required
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="w-full h-12 px-4 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#1A3C34]/20 transition-all"
                  data-testid="room-image-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1A202C' }}>
                    Price per Night (₹)
                  </label>
                  <input
                    type="number"
                    required
                    value={formData.price_per_night}
                    onChange={(e) => setFormData({ ...formData, price_per_night: e.target.value })}
                    className="w-full h-12 px-4 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#1A3C34]/20 transition-all"
                    data-testid="room-price-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1A202C' }}>
                    Max Guests
                  </label>
                  <input
                    type="number"
                    required
                    value={formData.max_guests}
                    onChange={(e) => setFormData({ ...formData, max_guests: e.target.value })}
                    className="w-full h-12 px-4 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#1A3C34]/20 transition-all"
                    data-testid="room-guests-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1A202C' }}>
                  Amenities (comma-separated)
                </label>
                <input
                  type="text"
                  value={formData.amenities}
                  onChange={(e) => setFormData({ ...formData, amenities: e.target.value })}
                  className="w-full h-12 px-4 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#1A3C34]/20 transition-all"
                  placeholder="WiFi, Breakfast, AC"
                  data-testid="room-amenities-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1A202C' }}>
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full h-12 px-4 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#1A3C34]/20 transition-all"
                  data-testid="room-status-input"
                >
                  <option value="available">Available</option>
                  <option value="occupied">Occupied</option>
                  <option value="maintenance">Maintenance</option>
                </select>
              </div>
              <div className="flex gap-4 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <button type="submit" className="btn-primary flex-1" data-testid="save-room-button">
                  {editingRoom ? 'Update' : 'Create'} Room
                </button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminRooms;